/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import _ from 'lodash';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';

import { VisuallyHidden } from '@coursera/cds-core';
import { email as isEmailInvalid } from '@coursera/coursera-ui/lib/utils/validation';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import SettingsServerActionCreators from 'bundles/account-settings/actions/SettingsServerActionCreators';
import SaveButton from 'bundles/account-settings/components/common/SaveButton';
import SettingsModal from 'bundles/account-settings/components/common/SettingsModal';
import StatusMessage from 'bundles/account-settings/components/common/StatusMessage';
import LanguageSelect from 'bundles/account-settings/components/sections/basic-information/LanguageSelect';
import TimezoneSelect from 'bundles/account-settings/components/sections/basic-information/TimezoneSelect';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';
import type { UserForcedSSOContextType } from 'bundles/account-settings/contexts/UserForcedSSOContext';
import { useUserForcedSSOContext } from 'bundles/account-settings/contexts/UserForcedSSOContext';
import { hasInvalidCharacters } from 'bundles/authentication/utils/nameVerificationUtils';
import { useNaptime } from 'bundles/naptimejs';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import BasicProfilesV1 from 'bundles/naptimejs/resources/basicProfiles.v1';
import Modal from 'bundles/phoenix/components/Modal';
import { USER_NAME_REGEX_PATTERN } from 'bundles/user-account/common/constants';
import {
  getClientSideMessages,
  getErrorMessages,
  getResponseErrorCode,
} from 'bundles/user-account/utils/errorMessageUtils';

import _t from 'i18n!nls/account-settings';

import 'css!./__styles__/BasicInformation';

const { Fields, UpdateStates, Sections } = SettingsConstants;

type OuterProps = {
  fullName: string | undefined;
  emailAddress: string | undefined;
  updateStatus: any;
  invalidFields: any[];
  updatedEmailAddress: string;
};

type BasicProfile = {
  profile: BasicProfilesV1;
};

type InnerProps = BasicProfile & UserForcedSSOContextType & OuterProps;

type State = {
  showEnterPasswordModal: boolean;
  isInProgress: boolean;
  errorCode: string;
  fullName: string | undefined;
  emailAddress: string | undefined;
  timezone: string;
  locale: string;
  password: string;
};

export class BasicInformation extends React.Component<InnerProps, State> {
  state = {
    showEnterPasswordModal: false,
    isInProgress: false,
    errorCode: '',
    fullName: this.props.fullName,
    emailAddress: this.props.emailAddress,
    timezone: this.props.profile.timezone,
    locale: this.props.profile.locale,
    password: '',
  };

  formRef: HTMLFormElement | null = null;

  setFormRef = (element: HTMLFormElement) => {
    this.formRef = element;
  };

  handleUpdateClick = () => {
    const { fullName, emailAddress, timezone, locale } = this.state;
    const newSettings = [];

    if (isEmailInvalid(emailAddress || '')) {
      SettingsServerActionCreators.updateInvalidEmail(Sections.BASIC_INFORMATION);
      return;
    }
    if (hasInvalidCharacters(fullName || '')) {
      SettingsServerActionCreators.updateFailedNameForbidden(Sections.BASIC_INFORMATION);
      return;
    } else {
      SettingsActionCreators.resetSection(Sections.BASIC_INFORMATION);
    }

    if (this.props.fullName !== fullName) {
      newSettings.push({
        type: Fields.FULL_NAME,
        value: fullName,
      });
    }

    if (this.props.emailAddress !== emailAddress) {
      this.setState({ showEnterPasswordModal: true });
    }

    if (this.props.profile.timezone !== timezone) {
      newSettings.push({
        type: Fields.TIMEZONE,
        value: timezone,
      });
    }

    if (this.props.profile.locale !== locale) {
      newSettings.push({
        type: Fields.LOCALE,
        value: locale,
      });
    }

    if (!_.isEmpty(newSettings)) {
      SettingsActionCreators.updateBasicInformation(newSettings, {
        userId: this.props.profile.id,
        externalId: this.props.profile.externalId,
        privacy: this.props.profile.privacy,
      });
    }
  };

  handleChangeEmail = () => {
    const { emailAddress, password } = this.state;
    const { profile } = this.props;

    this.setState({ isInProgress: true });

    SettingsActionCreators.updateEmailAddressWithPassword(emailAddress || '', password, profile.id)
      .then(() => {
        this.setState(() => ({
          showEnterPasswordModal: false,
          errorCode: '',
        }));
      })
      .catch((resp) => {
        this.setState(() => ({
          errorCode: getResponseErrorCode(resp.responseJSON.message),
        }));
      })
      .finally(() => {
        this.setState({ isInProgress: false });
      });
  };

  handleChange = (event: $TSFixMe) => {
    // @ts-expect-error TSMIGRATION-5.0
    this.setState({ [event.target.name]: event.target.value });
  };

  closeEnterPasswordModal = () => {
    this.setState({ showEnterPasswordModal: false, errorCode: '', password: '' });
  };

  renderValidation(field: $TSFixMe) {
    const { invalidFields } = this.props;

    if (_.includes(invalidFields, field)) {
      return <StatusMessage status={UpdateStates.FAILED} field={field} />;
    }
  }

  renderPasswordInputError() {
    const { errorCode } = this.state;
    const errorMessages = getErrorMessages;

    if (errorCode) {
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      return <p className="failed-error-message">{errorMessages[errorCode]}</p>;
    }
  }

  renderEnterPasswordModal() {
    const { isInProgress, password, showEnterPasswordModal } = this.state;
    const isPasswordEmpty = password.length === 0;

    return (
      showEnterPasswordModal && (
        <Modal
          modalName="EnterPasswordModal"
          className="rc-EnterPasswordModal"
          handleClose={this.closeEnterPasswordModal}
          allowClose={true}
        >
          <div>
            <h2 className="headline-2-text">
              {_t('To change your email address, please confirm your Coursera password.')}
            </h2>
            {this.renderPasswordInputError()}
            <input
              type="password"
              placeholder={_t('Enter your password...')}
              className="password-input"
              disabled={isInProgress}
              name="password"
              value={password}
              onChange={this.handleChange}
              required
            />
            <div className="password-input-actions">
              <button className="passive" onClick={this.closeEnterPasswordModal} type="button">
                {_t('Cancel')}
              </button>
              <button
                className="primary update-email-action"
                disabled={isPasswordEmpty || isInProgress}
                onClick={this.handleChangeEmail}
                type="button"
              >
                {isInProgress ? _t('Updating Email') : _t('Update Email')}
              </button>
            </div>
          </div>
        </Modal>
      )
    );
  }

  renderEmailConfirmationModal() {
    const { updatedEmailAddress } = this.props;

    if (updatedEmailAddress) {
      return (
        <SettingsModal title="Verification sent" section={Sections.BASIC_INFORMATION}>
          <FormattedHTMLMessage
            message={_t(
              `A verification email has been sent to {email}.
               It may take several minutes to appear in your inbox.`
            )}
            email={updatedEmailAddress}
          />
        </SettingsModal>
      );
    }
  }

  handleSubmitButton = () => {
    if (this.formRef) {
      // check to see if broser supports html-based form validation. Necessary
      // for use with the `pattern` prop on `#settings-basic-full-name`
      if (typeof this.formRef.checkValidity === 'function') {
        if (this.formRef.checkValidity()) this.handleUpdateClick();
      } else {
        // fall back to default submit functionality, without built-in validation.
        // `handleUpdateClick` validates emails and other info, so we should be safe.
        this.handleUpdateClick();
      }
    }
  };

  render() {
    const { fullName, emailAddress } = this.state;
    const { profile, updateStatus, isUserSsoLoginForced } = this.props;
    const clientSideMessages = getClientSideMessages();

    return (
      <>
        <VisuallyHidden component="h2" className="rc-A11yScreenReaderOnly">
          {_t('Basic Information')}
        </VisuallyHidden>
        <form action="javascript:void(0)" onSubmit={this.handleUpdateClick} ref={this.setFormRef}>
          <div className="rc-BasicInformation" data-e2e="basic-information">
            {this.renderEnterPasswordModal()}
            {this.renderEmailConfirmationModal()}

            <div className="horizontal-box wrap">
              <div className="settings-input-wrapper flex-3">
                <label htmlFor="settings-basic-full-name">{_t('Full name')}</label>
                <input
                  type="text"
                  id="settings-basic-full-name"
                  name="fullName"
                  value={fullName}
                  pattern={USER_NAME_REGEX_PATTERN}
                  title={clientSideMessages.NAME_VALIDATION}
                  onChange={this.handleChange}
                  autoComplete="name"
                  required
                />
                {this.renderValidation(Fields.FULL_NAME)}
              </div>

              <div className="settings-input-wrapper flex-3">
                <label htmlFor="settings-basic-email">{_t('Email address')}</label>
                <input
                  type="text"
                  id="settings-basic-email"
                  data-e2e="settings-basic-email"
                  name="emailAddress"
                  value={emailAddress}
                  onChange={this.handleChange}
                  autoComplete="email"
                  disabled={isUserSsoLoginForced}
                  required
                />
                {this.renderValidation(Fields.EMAIL_ADDRESS)}
              </div>
            </div>

            <div className="horizontal-box wrap">
              <div className="settings-input-wrapper flex-3">
                <label htmlFor="settings-basic-timezone">{_t('Timezone')}</label>
                <TimezoneSelect defaultValue={profile.timezone} onChange={this.handleChange} />
              </div>

              <div className="settings-input-wrapper flex-3">
                <label htmlFor="settings-basic-language">{_t('Language')}</label>
                <LanguageSelect defaultValue={profile.locale} onChange={this.handleChange} />
              </div>
            </div>

            <div className="save-button-wrapper horizontal-box align-items-vertical-center">
              <SaveButton
                onClick={this.handleSubmitButton}
                text={_t('Save')}
                updating={updateStatus.state === UpdateStates.UPDATING}
              />
              <StatusMessage status={updateStatus.state} />
            </div>
          </div>
        </form>
      </>
    );
  }
}

const BasicInformationWrapper: React.FC<OuterProps> = (props) => {
  const { isUserSsoLoginForced } = useUserForcedSSOContext();
  const {
    data: { profile },
    pending,
  } = useNaptime<BasicProfile>(() => ({
    profile: BasicProfilesV1.me({
      fields: ['externalId', 'privacy', 'locale', 'timezone'],
    }),
  }));

  if (!profile || pending) {
    return null;
  }

  return <BasicInformation {...props} profile={profile} isUserSsoLoginForced={isUserSsoLoginForced} />;
};

export default BasicInformationWrapper;
